import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useDataLayerValue } from "../../DataLayer/DataLayer";
import CustomizedTable from "../../components/UI/CustomizedTable";
import Logs from "../../components/Logs";
import SearchField from "../../components/SearchField";
import SwitchBar from "../../components/Pages/SwitchBar";
import { getReportDownloads, downloadReportFile } from "../../utils/ApiCalls/ReportDownloads";
import { addlog } from "../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../utils/Constant/LogConstant";
import { MODULE_LABELS, getReportFileName } from "../../utils/Constant/ReportDownloadLabels";

const STATUS = {
  0: { label: "Pending", className: "badge badge-warning" },
  1: { label: "Completed", className: "badge badge-success" },
  2: { label: "Processing", className: "badge badge-info" },
  3: { label: "Failed", className: "badge badge-danger" },
};

const ReportDownloads = ({ module }) => {
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [subTab, setSubTab] = useState("requests");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const getData = async (value, isLoading) => {
    if (isLoading) dispatch({ type: "SET_LOADING", loading: true });
    const data = await getReportDownloads({
      page: value || page,
      limit: pageSize,
      module,
      user_id: userDetails.id,
    });
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.count || 0);
    }
    if (isLoading) dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getData(page, true);
    const interval = setInterval(() => getData(page, false), 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleDownload = async (record) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const reportLabel = MODULE_LABELS[module] || module;
    const downloadName = getReportFileName(module, record.createdAt, record.id, record.timezone);
    try {
      const blob = await downloadReportFile(record.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addlog(
        LOG_ACTIONS.DOWNLOADED,
        LOG_TYPES.REPORT_DOWNLOADS,
        { title: `${reportLabel} downloaded`, moduleName: module, reportDownloadId: record.id, fileName: downloadName },
        userDetails.id
      );
    } catch (e) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: "Failed to download file", type: "error" } });
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const filteredRows = rows.filter((row) =>
    getReportFileName(module, row.createdAt, row.id, row.timezone)
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const columns = [
    {
      title: "Sl",
      width: 40,
      key: "sl",
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: "Requested At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (val) =>
        val
          ? moment(val)
              .tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
              .format("DD/MM/YYYY hh:mm a")
          : "-",
    },
    {
      title: "File Name",
      key: "fileName",
      render: (text, record) => getReportFileName(module, record.createdAt, record.id, record.timezone),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (val) => <span className={STATUS[val]?.className || "badge badge-secondary"}>{STATUS[val]?.label || "Unknown"}</span>,
    },
    {
      title: "Action",
      key: "operation",
      width: 100,
      render: (text, record) =>
        record.status === 1 ? (
          <button className="btn success_btn" onClick={() => handleDownload(record)}>
            <i className="fa fa-download" data-toggle="tooltip" title="Download"></i>
          </button>
        ) : record.status === 3 ? (
          <span title={record.errorMessage}>Failed</span>
        ) : (
          <i className="fa fa-spinner fa-spin" data-toggle="tooltip" title="In progress"></i>
        ),
    },
  ];

  return (
    <div className="container-fluid">
      <style>
        {`.report-downloads-subtabs ul.voucher_nav {
          border-bottom: 1px solid #e0e0e0;
          padding-left: 0;
        }
        .report-downloads-subtabs ul.voucher_nav li button.nav_btn {
          background: transparent !important;
          border: none !important;
          border-bottom: 2px solid transparent !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          color: #757575 !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          padding: 6px 4px !important;
          margin-right: 24px !important;
        }
        .report-downloads-subtabs ul.voucher_nav li button.nav_btn.active {
          color: var(--red, #d7182a) !important;
          border-bottom: 2px solid var(--red, #d7182a) !important;
        }`}
      </style>
      <div className="report-downloads-subtabs" style={{ marginBottom: "12px" }}>
        <SwitchBar
          tabs={[
            { title: "Requests", value: "requests" },
            { title: "Activity Logs", value: "activity" },
          ]}
          activeTab={subTab}
          setActiveTab={setSubTab}
        />
      </div>

      {subTab === "activity" ? (
        <Logs type={LOG_TYPES.REPORT_DOWNLOADS} moduleName={module} extendedColors />
      ) : (
        <div className="card">
          <div className="card-header two_item">
            <h5 className="card-title">Download Logs</h5>
            <div style={{ maxWidth: 220 }}>
              <SearchField
                value={search}
                setValue={(e) => setSearch(e.target.value)}
                SearchValue={() => {}}
                placeholder="Search by file name"
              />
            </div>
          </div>
          <div className="card-body">
            <div className="table_holder">
              <CustomizedTable
                setRows={setRows}
                setPageSize={setPageSize}
                setPage={setPage}
                pageSize={pageSize}
                page={page}
                count={count}
                columns={columns}
                rows={filteredRows}
                title={"Download Logs"}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDownloads;
